import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { projectsAPI, usersAPI, invitationsAPI } from '../services/api'
import { Building, Plus, X, ChevronDown, Send, Package, Sparkles, Globe, AlertCircle, ArrowLeft } from 'lucide-react'
import ProjectWizard from '../components/ProjectWizard'

const CHANNELS = [
  { id: 'tiktok',    label: 'TikTok',    formats: ['9:16 (Reels/TikTok)'] },
  { id: 'instagram', label: 'Instagram', formats: ['9:16 (Reels/TikTok)', '4:5 (Instagram)'] },
  { id: 'meta',      label: 'Meta',      formats: ['4:5 (Instagram)', '1:1 (Feed)'] },
]

// Concept-opslag: bewaart een half-ingevulde brief lokaal zodat een onverwachte
// reload niets wist. Gewist na succesvol plaatsen of handmatig via "Wissen".
const DRAFT_KEY = 'ugc4you:projectCreateDraft'

const ProjectCreatePage = ({ onNavigate } = {}) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const inviteCreatorId = searchParams.get('inviteCreator')
  const [loading, setLoading] = useState(false)
  const [projectId, setProjectId] = useState(null)
  const [showWizard, setShowWizard] = useState(false)
  const [wizardUsed, setWizardUsed] = useState(false)
  const [showMoreDetails, setShowMoreDetails] = useState(false)
  const [invitedCreator, setInvitedCreator] = useState(null)
  const [draftRestored, setDraftRestored] = useState(false)

  // Prefill brief-data van quick-brief widget op homepage (via sessionStorage).
  // Komt hier terecht na ?fromQuickBrief=1 of na register?intent=brief.
  // Geen pendingBrief? Dan herstellen we een eerder opgeslagen concept, zodat een
  // onverwachte reload (sessie-hapering, refresh) je ingevulde brief niet wist.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('pendingBrief')
      if (raw) {
        const pending = JSON.parse(raw)
        // Channels van homepage komen als 'TikTok', 'Instagram', etc. — map naar form ID's
        const channelMap = { 'TikTok': 'tiktok', 'Instagram': 'instagram', 'YouTube Shorts': 'tiktok', 'Meta Ads': 'meta' }
        const mappedChannels = Array.from(new Set((pending.channels || []).map(c => channelMap[c] || c.toLowerCase()).filter(Boolean)))
        setFormData(prev => ({
          ...prev,
          description: pending.description || prev.description,
          channels: mappedChannels.length > 0 ? mappedChannels : prev.channels,
          budget: pending.budget ? String(pending.budget) : prev.budget,
          deadline: pending.deadline || prev.deadline
        }))
        sessionStorage.removeItem('pendingBrief')
        return
      }
      const draftRaw = localStorage.getItem(DRAFT_KEY)
      if (draftRaw) {
        const draft = JSON.parse(draftRaw)
        if (draft && typeof draft === 'object') {
          setFormData(prev => ({ ...prev, ...draft }))
          setDraftRestored(true)
          return
        }
      }
      restoreServerDraft()
    } catch (e) { /* ignore */ }
  }, [])

  // Geen lokaal concept? Dan kan er nog een server-concept staan van een ander
  // apparaat of een gesloten tab. Pas als ook dat er niet is, start de assistent.
  const restoreServerDraft = async () => {
    try {
      const res = await projectsAPI.getDraft()
      const data = res.data?.draft?.data
      if (data && typeof data === 'object' && Object.keys(data).length > 0) {
        setFormData(prev => ({ ...prev, ...data }))
        setDraftRestored(true)
        return
      }
    } catch (e) { /* geen concept of niet bereikbaar — gewoon doorgaan */ }
    maybeOpenWizard()
  }

  // Wie nog nooit een opdracht plaatste begint hier met een leeg tekstvak van
  // minimaal 50 tekens — precies de horde waar de meeste bedrijven afhaken.
  // Voor die groep start de assistent daarom vanzelf: vijf keuzes en de brief
  // staat er. Wie al eens iets plaatste weet de weg en krijgt het formulier.
  const maybeOpenWizard = async () => {
    try {
      const res = await projectsAPI.getMyProjects({ limit: 1 })
      const total = res.data?.pagination?.totalProjects ?? res.data?.projects?.length ?? 0
      if (total === 0) setShowWizard(true)
    } catch (e) { /* bij twijfel: gewoon het formulier tonen */ }
  }

  // Als ?inviteCreator=<id> in URL: fetch creator info zodat we kunnen tonen
  // dat deze brief automatisch naar deze creator wordt uitgenodigd.
  useEffect(() => {
    if (!inviteCreatorId) return
    let cancelled = false
    usersAPI.getCreator(inviteCreatorId)
      .then(r => {
        if (cancelled) return
        const c = r.data.creator || r.data
        setInvitedCreator(c)
      })
      .catch(err => {
        console.error('Kon invited creator niet ophalen:', err)
      })
    return () => { cancelled = true }
  }, [inviteCreatorId])

  const [formData, setFormData] = useState({
    title: '', // optional override; auto-derived from description if empty
    description: '',
    channels: [], // multi-select chips
    category: '',
    contentType: 'video',
    contentRequirements: [],
    budget: '',
    deadline: '',
    location: '',
    websiteUrl: '',
    preferredCreatorType: '',
    specialRequirements: '',
    deliverables: [],
    timeline: { milestones: [] },
    willSendProduct: false,
    productReturnRequired: false,
    shootOnLocation: false
  })

  const [newRequirement, setNewRequirement] = useState('')
  const [newDeliverable, setNewDeliverable] = useState('')

  // Sla het concept lokaal op bij elke wijziging (alleen als er iets ingevuld is),
  // zodat een onverwachte page-reload de ingevulde brief niet wist.
  useEffect(() => {
    try {
      const hasContent = formData.description || formData.channels.length || formData.budget ||
                         formData.deadline || formData.websiteUrl || formData.title ||
                         formData.specialRequirements || formData.location
      if (hasContent) localStorage.setItem(DRAFT_KEY, JSON.stringify(formData))
    } catch (e) { /* ignore */ }
  }, [formData])

  // Daarnaast het concept naar de server, zodat het een gesloten tab of ander
  // apparaat overleeft. Ontkoppeld van de lokale opslag met een ruime debounce:
  // dit hoeft niet per toetsaanslag, één keer per paar seconden rust is genoeg.
  useEffect(() => {
    const hasContent = (formData.description || '').trim().length >= 20 ||
                       formData.channels.length > 0 || formData.budget
    if (!hasContent) return
    const timer = setTimeout(() => {
      projectsAPI.saveDraft(formData).catch(() => { /* stil: lokaal staat het al */ })
    }, 3000)
    return () => clearTimeout(timer)
  }, [formData])

  const handleWizardComplete = (wizardData) => {
    setFormData(prev => ({
      ...prev,
      description: wizardData.description || prev.description,
      contentRequirements: wizardData.contentRequirements?.length > 0 ? wizardData.contentRequirements : prev.contentRequirements,
      deliverables: wizardData.deliverables?.length > 0 ? wizardData.deliverables : prev.deliverables,
      budget: wizardData.budget ? String(wizardData.budget) : prev.budget,
      channels: wizardData.channels?.length > 0 ? wizardData.channels : prev.channels,
      deadline: wizardData.deadline || prev.deadline,
      willSendProduct: wizardData.willSendProduct === true
    }))
    setShowWizard(false)
    setWizardUsed(true)
  }


  useEffect(() => {
    if (!user || (user.role !== 'bedrijf' && user.role !== 'agency')) {
      onNavigate ? onNavigate('home') : navigate('/dashboard')
    }
  }, [user, navigate, onNavigate])

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const addRequirement = () => {
    if (newRequirement.trim()) {
      setFormData(prev => ({
        ...prev,
        contentRequirements: [...prev.contentRequirements, newRequirement.trim()]
      }))
      setNewRequirement('')
    }
  }

  const removeRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      contentRequirements: prev.contentRequirements.filter((_, i) => i !== index)
    }))
  }

  const addDeliverable = () => {
    if (newDeliverable.trim()) {
      setFormData(prev => ({
        ...prev,
        deliverables: [...prev.deliverables, {
          title: newDeliverable.trim(),
          description: ''
        }]
      }))
      setNewDeliverable('')
    }
  }

  const removeDeliverable = (index) => {
    setFormData(prev => ({
      ...prev,
      deliverables: prev.deliverables.filter((_, i) => i !== index)
    }))
  }

  const toggleChannel = (id) => {
    setFormData(prev => ({
      ...prev,
      channels: prev.channels.includes(id)
        ? prev.channels.filter(c => c !== id)
        : [...prev.channels, id]
    }))
  }

  // Derive a 5-100 char title from the description when user hasn't supplied one
  const deriveTitle = (desc) => {
    const clean = (desc || '').trim().replace(/\s+/g, ' ')
    if (!clean) return ''
    const sentence = clean.split(/[.!?\n]/)[0].trim()
    const base = sentence.length >= 5 ? sentence : clean
    return base.length > 90 ? base.slice(0, 87).trim() + '…' : base
  }

  // Map selected channels to allowed contentFormat values (deduped)
  const deriveContentFormats = (channelIds) => {
    const formats = new Set()
    channelIds.forEach(id => {
      const ch = CHANNELS.find(c => c.id === id)
      if (ch) ch.formats.forEach(f => formats.add(f))
    })
    if (formats.size === 0) formats.add('9:16 (Reels/TikTok)')
    return Array.from(formats)
  }

  // Binnen het dashboard blijven we in het dashboard en openen we het tabblad
  // Stuur naar het tabblad waar het ontbrekende veld daadwerkelijk staat.
  // Bedrijfsnaam (companyName) staat op Profiel; KvK staat op Facturatie. Eerder
  // ging dit altijd naar Facturatie omdat KvK toen nog een eis was — sindsdien is
  // die eis vervallen en belandde je dus op een tab zonder het veld dat je zocht.
  // Let op: het veld "Officiële naam" op Facturatie is legalName, een ánder veld
  // dan companyName; dat invullen lost de gate niet op.
  const goToProfileSettings = (missing) => {
    // Defensief: als onClick dit per ongeluk met een event aanroept, geen crash.
    const ontbreekt = Array.isArray(missing) ? missing : []
    const tab = ontbreekt.includes('KvK-nummer') ? 'billing' : 'profile'
    if (onNavigate) onNavigate('settings', null, tab)
    else navigate(`/profile?tab=${tab}`)
  }

  const handleSubmit = async () => {
    // Client-side validation — only what's truly required server-side
    const description = (formData.description || '').trim()
    if (description.length < 50) {
      alert('Geef minimaal 50 tekens omschrijving zodat creators weten wat je wilt.')
      return
    }
    if (description.length > 2000) {
      alert('De omschrijving mag maximaal 2000 tekens zijn.')
      return
    }
    if (formData.channels.length === 0) {
      alert('Kies minstens één kanaal (TikTok, Instagram of Meta).')
      return
    }
    const budgetNum = parseFloat(formData.budget)
    if (!budgetNum || budgetNum < 25) {
      alert('Het budget moet minimaal €25 zijn.')
      return
    }
    if (!formData.deadline) {
      alert('Kies een deadline.')
      return
    }
    const deadlineDate = new Date(formData.deadline)
    if (deadlineDate <= new Date()) {
      alert('De deadline moet in de toekomst liggen.')
      return
    }

    const finalTitle = (formData.title.trim().length >= 5)
      ? formData.title.trim().slice(0, 100)
      : deriveTitle(description) || 'UGC opdracht'

    let normalizedWebsite = formData.websiteUrl.trim()
    if (normalizedWebsite) {
      if (!/^https?:\/\//i.test(normalizedWebsite)) normalizedWebsite = 'https://' + normalizedWebsite
      try {
        // Validate URL — throws on invalid
        new URL(normalizedWebsite)
      } catch {
        alert('Voer een geldige website URL in (bijv. https://jouwbedrijf.nl) of laat het veld leeg.')
        return
      }
    }

    try {
      setLoading(true)

      const backendData = {
        title: finalTitle,
        description,
        contentType: formData.contentType || 'video',
        contentFormat: deriveContentFormats(formData.channels),
        budget: budgetNum,
        deadline: deadlineDate.toISOString(),
        category: formData.category || undefined,
        location: formData.location || undefined,
        tags: formData.channels.map(c => CHANNELS.find(x => x.id === c)?.label).filter(Boolean),
        contentRequirements: formData.contentRequirements,
        preferredCreatorType: formData.preferredCreatorType || undefined,
        specialRequirements: formData.specialRequirements || undefined,
        deliverables: formData.deliverables.map(d =>
          typeof d === 'string' ? { title: d, description: '' } : d
        ),
        timeline: formData.timeline,
        productInfo: {
          willSendProduct: formData.willSendProduct,
          website: normalizedWebsite || undefined
        },
        // Locatie werd voorheen als los veld gestuurd maar door de backend-whitelist
        // genegeerd; via desiredCreatorProfile komt hij in de bestaande filter- en
        // weergavemachinerie terecht.
        ...(formData.location.trim() ? {
          desiredCreatorProfile: {
            location: { cities: [formData.location.trim()], locationImportant: true }
          }
        } : {}),
        shootLocation: { mode: formData.shootOnLocation ? 'op_locatie' : 'thuis' },
        ...(formData.willSendProduct && formData.productReturnRequired ? {
          productShipping: { returnShipment: { required: true } }
        } : {}),
        // Directe creator-invite → niet publiek op de marketplace, alleen voor de uitgenodigde creator
        ...(inviteCreatorId && invitedCreator ? { visibility: { isPublic: false } } : {})
      }

      console.log('Sending project data:', backendData)
      const response = await projectsAPI.createProject(backendData)

      // 2026+ flow: brief is gratis. Direct naar "Mijn opdrachten" waar de brief live staat.
      // Betaling van de aanbetaling volgt pas zodra bedrijf een creator-voorstel accepteert.
      const createdProjectId = response.data.project?.id || response.data.project?._id

      // Brief geplaatst → concept wissen zodat het niet opnieuw verschijnt
      try { localStorage.removeItem(DRAFT_KEY) } catch (e) { /* ignore */ }
      projectsAPI.deleteDraft().catch(() => { /* concept verloopt anders vanzelf */ })

      // Als we vanaf een creator-profiel kwamen: stuur automatisch een invitation
      let inviteParam = ''
      if (createdProjectId && inviteCreatorId && invitedCreator) {
        try {
          const creatorFirstName = invitedCreator.firstName || invitedCreator.displayName?.split(' ')[0] || 'jou'
          const msg = `Hi ${creatorFirstName}, ik heb deze brief specifiek met jou in gedachten gemaakt. Kijk maar even of dit iets is voor jou — een reactie zou top zijn!`
          await invitationsAPI.create({
            projectId: createdProjectId,
            creatorId: inviteCreatorId,
            message: msg
          })
          inviteParam = `&invited=${encodeURIComponent(invitedCreator.firstName || invitedCreator.displayName || 'creator')}`
        } catch (invErr) {
          console.error('Auto-invitation faalde (project staat wel live):', invErr)
          alert('Brief is geplaatst, maar de uitnodiging naar de creator kon niet automatisch verstuurd worden. Je kunt nog handmatig uitnodigen via "Stel opdracht voor".')
        }
      }

      onNavigate ? onNavigate('my-projects') : navigate(`/my-projects?published=1${inviteParam}`)
    } catch (error) {
      console.error('Error creating project:', error)

      // Specifieke KvK / verificatie error: stuur direct naar profiel
      if (error.response?.data?.code === 'KVK_REQUIRED') {
        const ontbreekt = error.response.data.missing || []
        const waar = ontbreekt.includes('KvK-nummer')
          ? 'Facturatie'
          : 'Profiel'
        const goToProfile = window.confirm(
          `Voordat je een brief kunt plaatsen moet je je bedrijfsprofiel afronden.\n\n` +
          `Ontbrekend: ${ontbreekt.join(', ')}\n\n` +
          `Je vindt dit onder het tabblad "${waar}". Daar nu heen?`
        )
        if (goToProfile) {
          goToProfileSettings(ontbreekt)
        }
      } else if (error.response && error.response.data && error.response.data.message) {
        alert('Fout: ' + error.response.data.message);
      } else if (error.response && error.response.data && error.response.data.errors) {
        // Handle validation errors from backend
        const errorMessages = error.response.data.errors.map(err => err.msg).join('\n');
        alert('Validatiefouten:\n' + errorMessages);
      } else {
        alert('Er is een fout opgetreden bij het publiceren van de opdracht. Probeer het opnieuw.');
      }
    } finally {
      setLoading(false)
    }
  }

  const categories = [
    { value: 'beauty-cosmetics', label: 'Beauty & Cosmetica' },
    { value: 'fashion-clothing', label: 'Mode & Kleding' },
    { value: 'food-drinks', label: 'Eten & Drinken' },
    { value: 'health-fitness', label: 'Gezondheid & Fitness' },
    { value: 'technology', label: 'Technologie' },
    { value: 'travel-lifestyle', label: 'Reizen & Lifestyle' },
    { value: 'home-garden', label: 'Wonen & Tuin' },
    { value: 'business-services', label: 'Zakelijke Diensten' }
  ]

  const creatorTypes = [
    'Micro-influencer (1K-10K)',
    'Mid-tier influencer (10K-100K)',
    'Macro-influencer (100K-1M)',
    'Mega-influencer (1M+)',
    'Geen voorkeur'
  ]

  if (!user || (user.role !== 'bedrijf' && user.role !== 'agency')) {
    return null
  }

  // Verificatie-gate uit routes/projects.js: zonder deze velden weigert de backend een
  // live brief. Vooraf tonen, anders vult iemand het hele formulier in voor niets.
  // KvK staat hier bewust NIET bij — dat is pas bij betaling vereist.
  const verificatieOntbreekt = [
    !(user.companyName || user.agencyName) && 'bedrijfsnaam',
    !user.isEmailVerified && 'e-mailverificatie'
  ].filter(Boolean)

  return (
    <div className="font-['Inter'] min-h-screen bg-white text-[#0F172A]">
      {/* Project Wizard */}
      {showWizard && (
        <ProjectWizard
          onComplete={handleWizardComplete}
          onSkip={() => setShowWizard(false)}
        />
      )}

      {/* Header */}
      <header className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 sticky top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl">
        {onNavigate ? (
          <button onClick={() => onNavigate('home')} className="text-2xl font-black tracking-tighter text-[#0F172A]">
            UGC<span className="text-[#1D8BF8]">4you</span>
          </button>
        ) : (
          <Link to="/" className="text-2xl font-black tracking-tighter text-[#0F172A]">
            UGC<span className="text-[#1D8BF8]">4you</span>
          </Link>
        )}

        <nav className="hidden md:flex items-center gap-8">
          {onNavigate ? (
            <button onClick={() => onNavigate('projects')} className="hover:text-[#1D8BF8] transition font-medium">Opdrachten</button>
          ) : (
            <Link to="/projects" className="hover:text-[#1D8BF8] transition font-medium">Opdrachten</Link>
          )}
          {onNavigate ? (
            <button onClick={() => onNavigate('creators')} className="hover:text-[#1D8BF8] transition font-medium">Creators</button>
          ) : (
            <Link to="/creators" className="hover:text-[#1D8BF8] transition font-medium">Creators</Link>
          )}
          {onNavigate ? (
            <button onClick={() => onNavigate('home')} className="hover:text-[#1D8BF8] transition font-medium">Dashboard</button>
          ) : (
            <Link to="/dashboard" className="hover:text-[#1D8BF8] transition font-medium">Dashboard</Link>
          )}

          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm font-semibold">{user.contactPerson}</div>
              <div className="text-xs text-[#475569]">{user.companyName}</div>
            </div>
            <div className="w-10 h-10 bg-[#3EB489] rounded-full flex items-center justify-center">
              <Building className="w-5 h-5 text-white" />
            </div>
          </div>
        </nav>

        {/* Mobiele terugweg: de nav hierboven is verborgen onder md en het logo gaat naar de homepage */}
        {onNavigate ? (
          <button
            type="button"
            onClick={() => onNavigate('home')}
            className="md:hidden inline-flex items-center gap-1.5 px-2.5 py-2 min-h-[44px] rounded-lg text-sm font-medium text-[#0F172A] hover:text-[#1D8BF8] transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </button>
        ) : (
          <Link
            to="/dashboard"
            className="md:hidden inline-flex items-center gap-1.5 px-2.5 py-2 min-h-[44px] rounded-lg text-sm font-medium text-[#0F172A] hover:text-[#1D8BF8] transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </Link>
        )}
      </header>


      <div className="relative">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-28 sm:pb-8">
          {/* Verificatie-waarschuwing — vóóraf, zodat het formulier niet voor niets wordt ingevuld */}
          {verificatieOntbreekt.length > 0 && (
            <div className="mb-8 p-5 md:p-6 rounded-2xl bg-white border border-amber-300 shadow-sm">
              <div className="flex items-start gap-4">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#0F172A]">Rond eerst je bedrijfsprofiel af</p>
                  <p className="text-sm text-[#475569] mt-2 leading-relaxed">
                    Je kunt de brief hieronder alvast invullen, maar plaatsen lukt pas als
                    {' '}<strong className="text-[#0F172A]">{verificatieOntbreekt.join(' en ')}</strong>{' '}
                    is ingevuld. Dat voorkomt spam en geeft creators vertrouwen.
                    {verificatieOntbreekt.includes('bedrijfsnaam') && (
                      <> Je vindt dit onder <strong className="text-[#0F172A]">Profiel</strong> — niet
                      onder Facturatie: het veld "Officiële naam" daar is je factuurnaam en telt hier niet mee.</>
                    )}
                  </p>
                  <button
                    type="button"
                    onClick={() => goToProfileSettings(verificatieOntbreekt)}
                    className="inline-flex items-center gap-2 mt-5 px-4 py-2.5 rounded-xl border border-[#1D8BF8] text-[#1D8BF8] text-sm font-semibold hover:bg-[#1D8BF8] hover:text-white transition"
                  >
                    Profiel afronden
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Invitation banner — wanneer brief vanaf creator-profiel wordt gemaakt */}
          {invitedCreator && (
            <div className="mb-6 p-4 md:p-5 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200 flex items-center gap-4">
              {invitedCreator.profilePictureUrl ? (
                <img
                  src={invitedCreator.profilePictureUrl}
                  alt={invitedCreator.firstName || 'creator'}
                  className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover ring-2 ring-white shadow"
                />
              ) : (
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-lg">
                  {(invitedCreator.firstName?.[0] || invitedCreator.displayName?.[0] || '?').toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-emerald-900">
                  Deze brief wordt direct uitgenodigd voor {invitedCreator.firstName && invitedCreator.lastName
                    ? `${invitedCreator.firstName} ${invitedCreator.lastName}`
                    : invitedCreator.displayName || 'deze creator'}
                </p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  Direct na het plaatsen ontvangt {invitedCreator.firstName || 'de creator'} een persoonlijke uitnodiging. De brief is <strong>privé</strong> — alleen zichtbaar voor {invitedCreator.firstName || 'deze creator'}, niet op de marketplace voor anderen.
                </p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-black mb-1.5 sm:mb-2 leading-tight">
                {invitedCreator ? `Opdracht voor ${invitedCreator.firstName || invitedCreator.displayName || 'creator'}` : 'Nieuwe opdracht plaatsen'}
              </h1>
              <p className="text-[#475569] text-base sm:text-lg">
                Beschrijf wat je wilt, kies kanalen, stel je budget in — klaar.
              </p>
            </div>
            <button
              onClick={() => setShowWizard(true)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-[#1D8BF8]/30 text-[#1D8BF8] text-sm font-semibold rounded-xl hover:border-[#1D8BF8] hover:shadow-md transition flex-shrink-0 self-stretch sm:self-start"
            >
              <Sparkles className="w-4 h-4" />
              Laat het invullen
            </button>
          </div>

          {/* Form Content — single page */}
          <div className="relative">
            <div className="relative p-5 sm:p-8 rounded-2xl sm:rounded-3xl bg-white border border-gray-200 shadow-md space-y-6 sm:space-y-7">

              {/* ===== Concept hersteld melding ===== */}
              {draftRestored && (
                <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800">
                  <span>We hebben je eerder ingevulde concept hersteld.</span>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({ title: '', description: '', channels: [], category: '', contentType: 'video', contentRequirements: [], budget: '', deadline: '', location: '', websiteUrl: '', preferredCreatorType: '', specialRequirements: '', deliverables: [], timeline: { milestones: [] }, willSendProduct: false, productReturnRequired: false, shootOnLocation: false })
                      try { localStorage.removeItem(DRAFT_KEY) } catch (e) { /* ignore */ }
                      projectsAPI.deleteDraft().catch(() => { /* verloopt anders vanzelf */ })
                      setDraftRestored(false)
                    }}
                    className="text-amber-700 underline whitespace-nowrap hover:text-amber-900 font-medium"
                  >
                    Wissen
                  </button>
                </div>
              )}

              {/* ===== Assistent klaar: van "schrijven" naar "nakijken" ===== */}
              {wizardUsed && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-white border border-[#3EB489]/40">
                  <Sparkles className="w-5 h-5 text-[#3EB489] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-[#0F172A]">Je opdracht staat klaar</p>
                    <p className="text-sm text-[#475569] mt-1">
                      Omschrijving, kanalen, budget en deadline zijn ingevuld op basis van je antwoorden.
                      Kijk het na, pas aan wat je anders wilt, en plaats hem. Je betaalt pas als je een creator kiest.
                    </p>
                  </div>
                </div>
              )}

              {/* ===== Description (required) ===== */}
              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-2">
                  Wat heb je nodig? <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={5}
                  className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-base text-[#0F172A] placeholder-[#475569]/50"
                  placeholder="Bijv. Ik zoek 3 TikTok-video's voor onze nieuwe skincare lijn. Doelgroep: vrouwen 20-35 jaar. Authentieke ochtendroutine-stijl, geen scripted advertentie. We willen de focus op het serum en hydraterende effect."
                />
                <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 mt-1.5 text-xs text-[#475569]">
                  <span>Hoe concreter, hoe betere voorstellen je krijgt.</span>
                  <span className={`whitespace-nowrap ${formData.description.length >= 50 ? 'text-emerald-600' : 'text-[#475569]/60'}`}>
                    {formData.description.length} / 50+ tekens
                  </span>
                </div>
              </div>

              {/* ===== Channels (required) ===== */}
              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-2">
                  Voor welke kanalen? <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {CHANNELS.map(ch => {
                    const active = formData.channels.includes(ch.id)
                    return (
                      <button
                        key={ch.id}
                        type="button"
                        onClick={() => toggleChannel(ch.id)}
                        className={`px-4 py-2.5 rounded-full font-medium text-sm border transition ${
                          active
                            ? 'bg-[#3EB489] text-white border-[#1D8BF8] shadow-sm'
                            : 'bg-white text-[#0F172A] border-gray-200 hover:border-[#1D8BF8]/40'
                        }`}
                      >
                        {ch.label}
                      </button>
                    )
                  })}
                </div>
                <p className="mt-1.5 text-xs text-[#475569]">
                  Kies één of meer — we stemmen video-formaten (9:16, 4:5, 1:1) hier automatisch op af.
                </p>
              </div>

              {/* ===== Budget + Deadline (required) ===== */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0F172A] mb-2">
                    Budget (€ excl. BTW) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => handleInputChange('budget', e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-base text-[#0F172A]"
                    placeholder="Bijv. 500"
                    min="25"
                    step="0.01"
                  />
                  {formData.budget && parseFloat(formData.budget) > 0 && (
                    <p className="mt-1.5 text-xs text-[#475569]">
                      Totaal incl. 21% BTW: <span className="font-semibold text-[#0F172A]">€{(parseFloat(formData.budget) * 1.21).toFixed(2)}</span>
                    </p>
                  )}
                  {/* Directe feedback in plaats van pas een alert bij het plaatsen:
                      een te laag budget bleek een stil afhaakpunt (concept met €20
                      dat nooit verstuurd is). */}
                  {formData.budget && parseFloat(formData.budget) > 0 && parseFloat(formData.budget) < 25 && (
                    <p className="mt-1.5 text-xs text-red-600 font-medium">
                      Het minimumbudget is €25.
                    </p>
                  )}
                  {formData.budget && parseFloat(formData.budget) >= 25 && parseFloat(formData.budget) < 50 && (
                    <p className="mt-1.5 text-xs text-amber-600">
                      Tip: voor dit bedrag reageren weinig creators — de meeste video's kosten €75–150 per stuk.
                    </p>
                  )}
                  <p className="mt-2.5 text-xs text-[#475569]">
                    Dit bedrag geldt per creator. Kies je meerdere creators, dan betaal je per creator het bedrag van zijn of haar voorstel.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0F172A] mb-2">
                    Deadline <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => handleInputChange('deadline', e.target.value)}
                    min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                    className="w-full appearance-none min-h-[50px] text-left bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-base text-[#0F172A]"
                  />
                </div>
              </div>

              {/* ===== Company website (optional) ===== */}
              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-2">
                  Bedrijfswebsite <span className="text-[#475569] font-normal">(optioneel)</span>
                </label>
                <div className="relative">
                  <Globe className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#475569]/60 pointer-events-none" />
                  <input
                    type="url"
                    value={formData.websiteUrl}
                    onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl pl-11 pr-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-base text-[#0F172A] placeholder-[#475569]/50"
                    placeholder="https://jouwbedrijf.nl"
                    inputMode="url"
                    autoComplete="url"
                  />
                </div>
                <p className="mt-1.5 text-xs text-[#475569]">
                  Help creators je merk en product beter te begrijpen door je website te delen.
                </p>
              </div>

              {/* ===== Locatie (optioneel) ===== */}
              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-2">
                  Locatie <span className="text-[#475569] font-normal">(optioneel)</span>
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-base text-[#0F172A] placeholder-[#475569]/50"
                  placeholder="Bijv. Amsterdam (laat leeg voor heel Nederland)"
                />
                <label className="mt-2 flex items-center gap-2 text-sm text-[#475569] cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!formData.shootOnLocation}
                    onChange={(e) => handleInputChange('shootOnLocation', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#1D8BF8] focus:ring-[#1D8BF8]/50"
                  />
                  De opname vindt (deels) op locatie plaats, niet alleen bij de creator thuis
                </label>
              </div>

              {/* ===== Fysiek product + retour ===== */}
              <div>
                <label className="block text-sm font-semibold text-[#0F172A] mb-2">Product opsturen?</label>
                <button
                  type="button"
                  onClick={() => handleInputChange('willSendProduct', !formData.willSendProduct)}
                  className={`flex items-center gap-3 w-full p-3.5 rounded-xl border transition ${
                    formData.willSendProduct
                      ? 'bg-blue-50 border-[#1D8BF8]/50'
                      : 'bg-[#F8FAFC] border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    formData.willSendProduct ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <Package className={`w-4 h-4 ${formData.willSendProduct ? 'text-[#1D8BF8]' : 'text-[#475569]/60'}`} />
                  </div>
                  <div className="text-left flex-1">
                    <div className="text-sm font-medium">{formData.willSendProduct ? 'Ja, ik stuur een product op' : 'Nee, geen fysiek product'}</div>
                    <div className="text-xs text-[#475569]/70">
                      {formData.willSendProduct
                        ? 'Track & trace voer je in na het aanmaken.'
                        : 'Klik om aan te geven dat je een product opstuurt.'}
                    </div>
                  </div>
                  <div className={`w-11 h-6 rounded-full p-0.5 transition flex-shrink-0 ${formData.willSendProduct ? 'bg-[#3EB489]' : 'bg-gray-300'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${formData.willSendProduct ? 'translate-x-5' : 'translate-x-0'}`} />
                  </div>
                </button>

                {/* Retour na afloop — alleen relevant bij een fysiek product */}
                {formData.willSendProduct && (
                  <button
                    type="button"
                    onClick={() => handleInputChange('productReturnRequired', !formData.productReturnRequired)}
                    className={`mt-3 flex items-center gap-3 w-full p-3.5 rounded-xl border transition ${
                      formData.productReturnRequired
                        ? 'bg-blue-50 border-[#1D8BF8]/50'
                        : 'bg-[#F8FAFC] border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-left flex-1">
                      <div className="text-sm font-medium">
                        {formData.productReturnRequired ? 'Product moet retour na afloop' : 'Creator mag het product houden'}
                      </div>
                      <div className="text-xs text-[#475569]/70">
                        {formData.productReturnRequired
                          ? 'In de brief komt te staan dat het product eigendom van jouw bedrijf blijft en na de samenwerking wordt teruggestuurd. De retourstap verschijnt in de workspace.'
                          : 'Klik als het product na de samenwerking teruggestuurd moet worden (bijv. geleende kleding of schoenen).'}
                      </div>
                    </div>
                    <div className={`w-11 h-6 rounded-full p-0.5 transition flex-shrink-0 ${formData.productReturnRequired ? 'bg-[#3EB489]' : 'bg-gray-300'}`}>
                      <div className={`w-5 h-5 bg-white rounded-full transition-transform ${formData.productReturnRequired ? 'translate-x-5' : 'translate-x-0'}`} />
                    </div>
                  </button>
                )}
              </div>

              {/* ===== More details (collapsible, optional) ===== */}
              <div className="border-t border-gray-100 pt-5">
                <button
                  type="button"
                  onClick={() => setShowMoreDetails(v => !v)}
                  className="inline-flex items-center gap-2 min-h-[44px] -my-2.5 text-sm font-semibold text-[#1D8BF8] hover:text-[#1670c9] transition"
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${showMoreDetails ? 'rotate-180' : ''}`} />
                  Meer details toevoegen (optioneel)
                </button>

                {showMoreDetails && (
                  <div className="mt-5 space-y-5 p-5 bg-[#F8FAFC]/60 rounded-2xl border border-gray-100">

                    {/* Custom title */}
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">Eigen opdrachttitel</label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        maxLength={100}
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-base text-[#0F172A] placeholder-[#475569]/50"
                        placeholder={deriveTitle(formData.description) || 'We genereren automatisch een titel uit je omschrijving'}
                      />
                    </div>

                    {/* Content type */}
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">Type content</label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { v: 'video', l: 'Video' },
                          { v: 'foto',  l: 'Foto' },
                          { v: 'beide', l: 'Beide' },
                        ].map(opt => (
                          <button
                            key={opt.v}
                            type="button"
                            onClick={() => handleInputChange('contentType', opt.v)}
                            className={`px-4 py-2 rounded-full text-sm font-medium border transition ${
                              formData.contentType === opt.v
                                ? 'bg-[#3EB489] text-white border-[#1D8BF8]'
                                : 'bg-white text-[#0F172A] border-gray-200 hover:border-[#1D8BF8]/40'
                            }`}
                          >
                            {opt.l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">Categorie</label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-base text-[#0F172A]"
                      >
                        <option value="">Selecteer een categorie</option>
                        {categories.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Creator type */}
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">Gewenste creator-type</label>
                      <select
                        value={formData.preferredCreatorType}
                        onChange={(e) => handleInputChange('preferredCreatorType', e.target.value)}
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-base text-[#0F172A]"
                      >
                        <option value="">Geen voorkeur</option>
                        {creatorTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    {/* Special requirements */}
                    <div>
                      <label className="block text-sm font-medium text-[#0F172A] mb-2">Speciale eisen / merkrichtlijnen</label>
                      <textarea
                        value={formData.specialRequirements}
                        onChange={(e) => handleInputChange('specialRequirements', e.target.value)}
                        rows={3}
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-[#1D8BF8]/50 focus:border-[#1D8BF8] text-base text-[#0F172A] placeholder-[#475569]/50"
                        placeholder="Bijv. logo zichtbaar in eerste 3s, geen alcohol/scheldwoorden, gebruik hashtag #merknaam."
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ===== Submit (desktop in-flow) ===== */}
              <div className="pt-2 hidden sm:block">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full px-6 py-4 bg-[#3EB489] text-white rounded-2xl font-semibold hover:bg-[#359E78] transition inline-flex items-center justify-center disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Publiceren...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Brief gratis plaatsen
                    </>
                  )}
                </button>
                <p className="mt-2 text-xs text-center text-[#475569]">
                  Geen verplichtingen. Pas 10% aanbetaling zodra je een voorstel accepteert — 90% bij goedkeuring van de content.
                </p>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ===== Sticky mobile CTA — altijd binnen duimbereik ===== */}
      <div className="sm:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-xl border-t border-gray-200 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="w-full px-6 py-3.5 bg-[#3EB489] text-white rounded-2xl font-semibold hover:bg-[#359E78] transition inline-flex items-center justify-center disabled:opacity-60 shadow-lg shadow-[#3EB489]/25"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
              Publiceren...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Brief gratis plaatsen
            </>
          )}
        </button>
        <p className="mt-1.5 text-[11px] leading-tight text-center text-[#475569]">
          Gratis plaatsen — pas 10% aanbetaling bij accepteren van een voorstel.
        </p>
      </div>
    </div>
  )
}

export default ProjectCreatePage